/**
 * P5 코호트 — Kaplan-Meier 생존곡선 (DESIGN §3.3.1.1 · RUNBOOK P7-2).
 *
 * **이 페이지의 주제는 곡선이 아니라 중도절단이다.** §3.3.1.1 이 기록한 사고가
 * 이 페이지의 존재 이유다: STALE 임계에서 잘린 세션을 '종료'로 세자 MTTR 중위값이
 * **1,440분 — 임계값 그 자체** 로 나왔다. 고친 뒤에는 71.9분이었다. 지표가
 * 데이터가 아니라 우리 설정을 반영하고 있으면 그 지표는 틀린 것이다.
 *
 * 그래서 화면 구성이 이렇다:
 *
 *   ① 생존곡선   — 절단을 정식으로 처리하면 고장은 언제 끝나는가
 *   ② 절단 구성  — 그 곡선이 **무엇을 못 봤는가** (두 절단은 다른 사건이다)
 *
 * 곡선이 먼저인 이유는 사람들이 곡선을 보러 오기 때문이고, 절단 구성이 바로
 * 아래인 이유는 곡선만 보고 나가면 안 되기 때문이다. 도입부에서 먼저 절단을
 * 말해 두면 곡선을 읽는 동안 그 전제가 유지된다.
 *
 * **비교 축은 페이지가 하나만 갖는다** (§8.6.4). 위젯마다 축을 두면 같은 화면의
 * 두 카드가 서로 다른 대상을 말한다.
 */

import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { Section } from '@/components/Section'
import { fetchEnvelope } from '@/lib/api'
import type { Envelope } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'
import { CensoringPanel } from './cohort/CensoringPanel'
import { SurvivalCurves } from './cohort/SurvivalCurves'
import './CohortPage.css'

/** KM 계단 한 칸. `at_risk` 가 함께 오는 것이 §7.2 계약이다 */
export interface KMStep {
  /** 사건·절단이 일어난 시각 (분) */
  t: number
  /** 그 시각 **이후** 의 생존확률 — 계단은 여기서 다음 t 까지 평평하다 */
  survival: number
  /** 그 시각 직전까지 남아 있던 대상 수 */
  at_risk: number
  events: number
  censored: number
}

/**
 * 한 그룹의 KM 결과.
 *
 * `group === null` 은 **그룹 값이 결측인 코호트**다 (실측 제조사 결측 37.6%).
 * 다른 그룹에 섞지도 버리지도 않으므로 화면에서도 하나의 그룹으로 남긴다.
 */
export interface KMCurve {
  group: string | null
  n_subjects: number
  n_events: number
  n_censored: number
  /** `observation_end`(관측 창 끝) · `stale_truncated`(연계 중단) · `unknown` */
  censored_by: Record<string, number>
  event_share: number | null
  /** `median_reached=false` 면 null — "0" 이 아니라 "관측 창 안에서 못 봤다" */
  median_survival: number | null
  median_reached: boolean
  verdict: 'estimated' | 'insufficient_events'
  reason: string | null
  steps: KMStep[]
  /** 서버가 계단을 추리기 **전** 개수. steps.length 와 다르면 추린 것이다 */
  steps_total: number
}

/**
 * `/cohort/survival` 응답.
 *
 * `curve_fitted=false` 인 응답이 정상 경로에 있다 — 세션이 조회 상한에 걸리면
 * 서버가 **적합을 거부한다**. 잘린 표본으로 그린 KM 은 조용히 틀린 곡선이라
 * 그리지 않는 편이 옳다. 그때는 아래 필드 대부분이 오지 않으므로 선택 필드로 둔다.
 */
export interface SurvivalData {
  group_by: string
  curves: KMCurve[]
  curve_fitted: boolean
  time_unit?: string
  /** 곡선을 그릴 최소 사건 수 (실측 10) */
  min_events?: number
  n_rows?: number
  n_invalid_duration?: number
  n_inconsistent_censor?: number
  n_withheld_groups?: number
  notes?: string[]
}

/**
 * 비교 축 — 서버가 받는 `group_by` 그대로다.
 *
 * 값은 `RANK_ENTITIES` 의 키와 같아 `useEntityLabel` 로 코드→이름을 붙일 수 있다
 * (`chger_type` 은 '02' 같은 코드로 온다).
 */
const COHORT_AXES = [
  { key: 'maker', label: '제조사' },
  { key: 'year', label: '설치 연식' },
  { key: 'chger_type', label: '충전기 타입' },
  { key: 'busi', label: '사업자' },
] as const

export type CohortAxis = (typeof COHORT_AXES)[number]['key']

export function CohortPage() {
  const filters = useFilters()
  const [axisKey, setAxisKey] = useState<CohortAxis>('maker')
  const axis = COHORT_AXES.find((a) => a.key === axisKey) ?? COHORT_AXES[0]

  const q = useQuery({
    queryKey: ['cohort/survival', axisKey, filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<SurvivalData>(
        '/cohort/survival',
        toQueryFilters(filters),
        { group_by: axisKey },
        signal,
      ),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<SurvivalData> | undefined
  // 서버가 붙인 주의를 삼키지 않는다 — "중위 생존이 STALE 임계에 붙은 그룹" 경고가
  // 여기로 온다. 그 경고가 곧 §3.3.1.1 사고의 재발 감지기다
  const serverNotes = env?.meta.notes ?? []

  const shared = {
    env,
    axis,
    // `isLoading` 이 아니라 `isPending` 이다. 요청이 실패해 재시도를 기다리는 동안
    // `isLoading` 은 false 이고 error 는 아직 null 이라, 그 틈에 카드가
    // **"조건에 맞는 데이터가 없습니다"** 를 띄운다 — 서버가 500 을 준 것을
    // "데이터가 없다"로 바꿔 말하는 셈이다. 값이 아직 없으면 "불러오는 중"이 맞다
    isLoading: q.isPending,
    error: q.error as Error | null,
    onRetry: () => void q.refetch(),
  }

  return (
    <div className="cohort">
      <header className="cohort__intro">
        <p className="cohort__lead">
          고장이 난 뒤 <strong>얼마 만에 복구되는가</strong>를 생존분석으로 봅니다. 핵심은 곡선이
          아니라 <strong>중도절단</strong>입니다 — 연계가 끊겨(STALE) 잘린 세션을 '복구'로 세면
          중위값이 <strong>1,440분(= STALE 임계값 그 자체)</strong>으로 나옵니다. 실제로 그
          버그가 있었고, 절단을 정식으로 처리한 뒤 <strong>71.9분</strong>이 되었습니다 (§3.3.1.1).
        </p>

        <div className="cohort__axis" role="group" aria-label="비교 축">
          <span className="cohort__axislabel">비교 축</span>
          {COHORT_AXES.map((a) => (
            <button
              key={a.key}
              type="button"
              className={`cohort__axisbtn${axisKey === a.key ? ' is-on' : ''}`}
              aria-pressed={axisKey === a.key}
              onClick={() => setAxisKey(a.key)}
            >
              {a.label}
            </button>
          ))}
          <span className="cohort__axishint">
            아래 두 카드가 함께 <strong>{axis.label}</strong> 기준으로 바뀝니다
          </span>
        </div>

        <p className="cohort__slow">
          축이나 고장 정의(모드)를 바꾸면 서버가 곡선을 <strong>다시 적합</strong>합니다 — 표본이
          큰 조합은 첫 조회가 십수 초 걸립니다 (실측 이용자 관점 × 제조사 16.5초). 값이 바뀌는
          동안 이전 숫자를 보여주지 않고 "불러오는 중"으로 둡니다.
        </p>

        {serverNotes.length > 0 && (
          <details className="cohort__notes">
            <summary>
              서버가 이 응답에 붙인 주의 <span className="num">{serverNotes.length}</span>건
            </summary>
            <ul>
              {serverNotes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </details>
        )}
      </header>

      <Section
        index={1}
        title="고장은 언제 끝나는가"
        lead="복구를 관측하지 못한 세션은 버리지도 종료로 세지도 않는다. Kaplan-Meier 는 그 세션을 '그 시각까지는 살아 있었다'는 정보로만 쓴다."
        layout="full"
      >
        <SurvivalCurves {...shared} />
      </Section>

      <Section
        index={2}
        title="그 곡선은 무엇을 못 봤는가"
        lead="관측 창이 끝난 것과 연계가 끊긴 것은 다른 사건이다. 둘 다 '복구를 못 봤다'이지만 원인이 다르므로 개수를 나눠 보여준다."
        layout="full"
      >
        <CensoringPanel {...shared} />
      </Section>
    </div>
  )
}
